import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Alert, Button, ToggleButton, ToggleButtonGroup,
  Divider, FormControlLabel, Switch, Chip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { api } from '../../../api/client.js';
import { UserRole } from '../../../../shared/types.js';
import { DB_ENVIRONMENTS, dbEnvironmentLabel, type DbProfile } from '../../../../shared/dbEnvironments.js';
import { PLAYWRIGHT_TARGET_LABEL, type PlaywrightBotStatus, type PlaywrightTarget } from '../../../../shared/playwrightTarget.js';
import { DB_PROFILE_CHANGED_EVENT } from '../../../components/DbEnvironmentBadge.js';
import { colors } from '../../../styles/theme.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { patchUser } from '../../../store/authSlice.js';
import { setConfiguratorVisible } from '../../../store/uiConfigSlice.js';
import type { AppDispatch, RootState } from '../../../store/index.js';

const CONFIGURATOR_VISIBLE_KEY = 'configurator_visible';

interface EnvironmentInfo {
  profile: DbProfile;
  label: string;
  hint: string;
  color: string;
  available: boolean;
  /** Zugangsdaten stehen in der .env. */
  configured: boolean;
  /** Verbindung beim Serverstart hergestellt. */
  connected: boolean;
  instance?: string;
  error?: string;
}

interface ProfileResponse {
  profile: DbProfile;
  global_profile: DbProfile;
  has_override: boolean;
  override_count: number;
  environments: EnvironmentInfo[];
}

/** Fallback, solange der Server noch nicht geantwortet hat. */
const FALLBACK_ENVIRONMENTS: EnvironmentInfo[] = DB_ENVIRONMENTS.map((e) => ({
  ...e, available: false, configured: false, connected: false,
}));

export default function GeneralSettingsPage() {
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState<string | null>(null);

  const [environments, setEnvironments] = useState<EnvironmentInfo[]>(FALLBACK_ENVIRONMENTS);

  const [dbProfile, setDbProfile] = useState<DbProfile>('prod');
  const [dbProfileServer, setDbProfileServer] = useState<DbProfile>('prod');
  const [dbSaving, setDbSaving] = useState(false);
  const [dbSaved, setDbSaved] = useState(false);

  const [globalProfile, setGlobalProfile] = useState<DbProfile>('prod');
  const [globalPick, setGlobalPick] = useState<DbProfile>('prod');
  const [overrideCount, setOverrideCount] = useState(0);
  const [globalDialogOpen, setGlobalDialogOpen] = useState(false);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalResult, setGlobalResult] = useState<{ profile: DbProfile; cleared: number } | null>(null);

  const [devMode, setDevMode] = useState(false);
  const [devModeSaving, setDevModeSaving] = useState(false);

  const configuratorVisible = useSelector((s: RootState) => s.uiConfig.configuratorVisible);
  const [configVisSaving, setConfigVisSaving] = useState(false);

  // Playwright-Bot (Seitenscanner): Live oder Staging — gilt fuer die Datenbank-Umgebung der Session.
  const [bot, setBot] = useState<PlaywrightBotStatus | null>(null);
  const [botSaving, setBotSaving] = useState(false);
  const [botSaved, setBotSaved] = useState<string | null>(null);

  const loadBot = () =>
    api.get<PlaywrightBotStatus>('/admin/playwright-bot').then((res) => {
      if (res.success) setBot(res.data); else setError(res.error.message);
    });

  useEffect(() => { void loadBot(); }, []);

  const handleToggleBot = async () => {
    if (!bot) return;
    const next: PlaywrightTarget = bot.target === 'staging' ? 'live' : 'staging';
    setBotSaving(true);
    setBotSaved(null);
    const res = await api.put<PlaywrightBotStatus>('/admin/playwright-bot', { target: next });
    setBotSaving(false);
    if (res.success) {
      setBot(res.data);
      setBotSaved(
        `${dbEnvironmentLabel(res.data.db_profile as DbProfile)} scannt ab dem nächsten Anstoß über den ` +
        `${PLAYWRIGHT_TARGET_LABEL[res.data.target]}-Bot.`,
      );
    } else {
      setError(res.error.message);
    }
  };

  const loadProfiles = () =>
    api.get<ProfileResponse>('/admin/database-profile').then((res) => {
      if (!res.success) return;
      setDbProfile(res.data.profile);
      setDbProfileServer(res.data.profile);
      setGlobalProfile(res.data.global_profile);
      setGlobalPick(res.data.global_profile);
      setOverrideCount(res.data.override_count);
      if (res.data.environments?.length) setEnvironments(res.data.environments);
    });

  useEffect(() => { loadProfiles(); }, []);

  // Konfigurator-Schalter kommt aus den App-Settings — frisch laden, damit der Schalter stimmt.
  useEffect(() => {
    api.get<{ settings: Array<{ key: string; value: string }> }>('/admin/settings').then((res) => {
      if (!res.success) return;
      const cv = res.data.settings.find((s) => s.key === CONFIGURATOR_VISIBLE_KEY);
      dispatch(setConfiguratorVisible(cv ? cv.value !== 'false' : true));
    });
  }, [dispatch]);

  // dev_mode frisch vom Server (vermeidet veralteten Redux-Stand)
  useEffect(() => {
    if (!user?.id) return;
    api.get<{ user: { dev_mode?: boolean } }>('/auth/me')
      .then((res) => { if (res.success) setDevMode(res.data.user.dev_mode ?? false); })
      .catch(() => { setDevMode(user.dev_mode ?? false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleProfileChange = (_: React.MouseEvent<HTMLElement>, value: DbProfile | null) => {
    if (!value) return;
    setDbProfile(value);
    setDbSaved(false);
  };

  const handleSaveProfile = async () => {
    setDbSaving(true);
    const res = await api.patch<{ profile: DbProfile }>('/admin/database-profile', { profile: dbProfile });
    setDbSaving(false);
    if (res.success) {
      setDbProfileServer(res.data.profile);
      setDbSaved(true);
      loadProfiles();
      // Anzeige in der Menueleiste nachziehen — sonst steht dort die alte Umgebung.
      window.dispatchEvent(new Event(DB_PROFILE_CHANGED_EVENT));
    } else {
      setError(res.error.message);
    }
  };

  const handleConfirmGlobal = async () => {
    setGlobalSaving(true);
    const res = await api.put<{ global_profile: DbProfile; cleared_overrides: number }>(
      '/admin/database-profile/global', { profile: globalPick },
    );
    setGlobalSaving(false);
    setGlobalDialogOpen(false);
    if (res.success) {
      setGlobalResult({ profile: res.data.global_profile, cleared: res.data.cleared_overrides });
      loadProfiles();
      // Der globale Wechsel loescht auch die eigene Auswahl — die Leiste muss mit.
      window.dispatchEvent(new Event(DB_PROFILE_CHANGED_EVENT));
    } else {
      setError(res.error.message);
    }
  };

  const handleToggleDevMode = async () => {
    if (!user) return;
    setDevModeSaving(true);
    const res = await api.patch<{ dev_mode: boolean }>(`/admin/users/${user.id}/dev-mode`, {});
    setDevModeSaving(false);
    if (res.success) {
      // Redux-User aktualisieren — Menueleiste und dev_only-Schalter reagieren dadurch sofort.
      dispatch(patchUser({ dev_mode: res.data.dev_mode }));
      setDevMode(res.data.dev_mode);
    } else {
      setError(res.error.message);
    }
  };

  const handleToggleConfigurator = async () => {
    const next = !configuratorVisible;
    setConfigVisSaving(true);
    const res = await api.patch(`/admin/settings/${CONFIGURATOR_VISIBLE_KEY}`, { value: next ? 'true' : 'false' });
    setConfigVisSaving(false);
    if (res.success) {
      // Store aktualisieren → Konfigurator-Menuepunkt erscheint/verschwindet sofort, ohne F5.
      dispatch(setConfiguratorVisible(next));
    } else {
      setError(res.error.message);
    }
  };

  const profileDirty = dbProfile !== dbProfileServer;
  const globalDirty = globalPick !== globalProfile;
  const unavailable = environments.filter((e) => !e.available);

  /** Ein Schalter je Umgebung — nicht eingerichtete sind gesperrt. */
  const envButtons = (selected: DbProfile, onChange: (_: React.MouseEvent<HTMLElement>, v: DbProfile | null) => void) => (
    <ToggleButtonGroup value={selected} exclusive onChange={onChange} size="small" sx={{ flexWrap: 'wrap' }}>
      {environments.map((e) => (
        <ToggleButton
          key={e.profile}
          value={e.profile}
          disabled={!e.available}
          sx={{
            px: 2.5,
            '&.Mui-selected': {
              bgcolor: e.color,
              color: colors.black,
              '&:hover': { bgcolor: e.color, filter: 'brightness(0.92)' },
            },
          }}
        >
          {e.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ── Datenbank-Umgebung: nur die eigene Session ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Datenbank-Umgebung</Typography>
        <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
          Wähle die Datenbank für deine Session. Andere Benutzer sind nicht betroffen.
          Aktueller Standard für alle: <strong>{dbEnvironmentLabel(globalProfile)}</strong>.
          {globalProfile === 'prod' && (
            <>
              {' '}„Standard" ist die Verbindung, mit der der Server gestartet wurde — auf einem deployten
              Dienst die Datenbank seiner eigenen Umgebung, lokal die aus <code>CLOUD_*</code> der <code>.env</code>.
            </>
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {envButtons(dbProfile, handleProfileChange)}

          <Button
            variant="contained"
            size="small"
            disabled={!profileDirty || dbSaving}
            onClick={handleSaveProfile}
            sx={{
              bgcolor: colors.lime,
              color: colors.black,
              '&:hover': { bgcolor: '#b8e600' },
              '&.Mui-disabled': { bgcolor: colors.lightGrey },
            }}
          >
            Speichern
          </Button>
        </Box>

        {unavailable.map((e) => (
          <Alert key={e.profile} severity={e.configured ? 'warning' : 'info'} sx={{ mt: 2 }}>
            <strong>{e.label}</strong>{' '}
            {e.configured ? (
              <>
                ist hinterlegt ({e.instance}), die Verbindung kam beim Serverstart aber nicht zustande:
                <Box component="code" sx={{ display: 'block', mt: 0.5, fontSize: 12 }}>{e.error}</Box>
              </>
            ) : (
              <>
                ist nicht eingerichtet — in der <code>.env</code> fehlen Instanz, IAM-Nutzer und
                Service-Account-Pfad.
              </>
            )}
          </Alert>
        ))}

        {dbSaved && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Profil gewechselt. Alle deine Anfragen nutzen jetzt die{' '}
            <strong>{dbEnvironmentLabel(dbProfileServer)}</strong>-Datenbank.
          </Alert>
        )}
      </Paper>

      {/* ── Globale Umgebung: gilt für alle Nutzer ── */}
      {user?.role === UserRole.ADMIN && (
        <Paper elevation={0} sx={{ border: `1px solid #f59e0b`, p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningAmberIcon sx={{ color: '#f59e0b' }} fontSize="small" />
            <Typography variant="h6">Hauptdatenbank für alle Nutzer</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
            Setzt die Datenbank-Umgebung für <strong>jeden Benutzer</strong> der Anwendung. Persönliche
            Auswahlen werden dabei aufgehoben. Gedacht für den Moment, in dem eine getestete Umgebung
            (z.&nbsp;B. Prod) zur produktiven Hauptdatenbank wird.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {envButtons(globalPick, (_, v) => { if (v) { setGlobalPick(v); setGlobalResult(null); } })}

            <Chip
              size="small"
              label={`Aktuell: ${dbEnvironmentLabel(globalProfile)}`}
              sx={{ bgcolor: colors.lightGrey }}
            />

            <Button
              variant="contained"
              size="small"
              disabled={!globalDirty || globalSaving}
              onClick={() => setGlobalDialogOpen(true)}
              sx={{
                bgcolor: '#f59e0b',
                color: colors.black,
                '&:hover': { bgcolor: '#d97706' },
                '&.Mui-disabled': { bgcolor: colors.lightGrey },
              }}
            >
              Für alle umstellen
            </Button>
          </Box>

          {overrideCount > 0 && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: colors.black60 }}>
              {overrideCount} Benutzer {overrideCount === 1 ? 'hat' : 'haben'} aktuell eine eigene Umgebung gewählt —
              beim Umstellen {overrideCount === 1 ? 'wird sie' : 'werden sie'} auf die Hauptdatenbank zurückgesetzt.
            </Typography>
          )}

          {globalResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Hauptdatenbank ist jetzt <strong>{dbEnvironmentLabel(globalResult.profile)}</strong>.
              {globalResult.cleared > 0
                ? ` ${globalResult.cleared} persönliche Auswahl(en) wurden aufgehoben.`
                : ' Es gab keine abweichenden persönlichen Auswahlen.'}
            </Alert>
          )}
        </Paper>
      )}

      {/* ── Playwright-Bot: Live oder Staging — gilt für die Datenbank-Umgebung ── */}
      {user?.role === UserRole.ADMIN && (
        <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Playwright-Bot (Seitenscanner)</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
            Welcher Scanner die Seiten lädt: der <strong>Live</strong>-Bot (Standard) oder der{' '}
            <strong>Staging</strong>-Bot. Gilt für <strong>alle Scan-Anstöße dieser Datenbank-Umgebung</strong> —
            Domain-Screener, Watchdog Raw Run, Discovery und GTM-Analyst — ab dem nächsten Anstoß. n8n bekommt
            nur das Wort (<code>playwright_target</code>) und kennt die beiden Adressen selbst.
          </Typography>

          {bot === null ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={bot.target === 'staging'}
                      onChange={handleToggleBot}
                      disabled={botSaving}
                      color="primary"
                      inputProps={{ 'aria-label': 'Staging-Bot für diese Umgebung verwenden' }}
                    />
                  }
                  label={bot.target === 'staging' ? 'Staging-Bot aktiv' : 'Live-Bot aktiv'}
                />
                <Chip
                  size="small"
                  label={`gilt für ${dbEnvironmentLabel(bot.db_profile as DbProfile)}`}
                  sx={{ bgcolor: colors.lightGrey }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={bot.source === 'default' ? 'Standard (nichts hinterlegt)' : 'in app_settings gespeichert'}
                />
              </Box>

              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Wirksame Adresse:{' '}
                <Box component="code" sx={{ fontSize: 13 }}>
                  {bot.base_url ?? '– nicht in der .env dieser Umgebung hinterlegt –'}
                </Box>
              </Typography>

              {!bot.base_url && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  Für <strong>{PLAYWRIGHT_TARGET_LABEL[bot.target]}</strong> fehlt{' '}
                  <code>{bot.target === 'staging' ? 'PLAYWRIGHT_BASE_STAGING' : 'PLAYWRIGHT_BASE_LIVE'}</code> in der{' '}
                  <code>.env</code> dieser Umgebung. Das Wort <code>playwright_target</code> reist trotzdem mit — n8n
                  nimmt dann seine eigene Adresse für {PLAYWRIGHT_TARGET_LABEL[bot.target]}.
                </Alert>
              )}

              {bot.target === 'staging' && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Diese Umgebung scannt über den <strong>Staging</strong>-Bot. Zum Zurückstellen auf Live den
                  Schalter ausschalten.
                </Alert>
              )}

              {botSaved && <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setBotSaved(null)}>{botSaved}</Alert>}
            </>
          )}
        </Paper>
      )}

      {/* ── Entwicklermodus (nur für Admins) ── */}
      {user?.role === UserRole.ADMIN && (
        <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Entwicklermodus</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: colors.black60 }}>
            Im Entwicklermodus werden neue Scans automatisch auf <strong>privat</strong> gesetzt, und
            Entwickler-exklusive Templates sind sichtbar. Nur für Admins.
          </Typography>
          <FormControlLabel
            control={<Switch checked={devMode} onChange={handleToggleDevMode} disabled={devModeSaving} color="primary" />}
            label={devMode ? 'Aktiv' : 'Inaktiv'}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Konfigurator sichtbar</Typography>
          <Typography variant="body2" sx={{ mb: 1, color: colors.black60 }}>
            Steuert den Menüpunkt <strong>Konfigurator</strong> in der oberen Leiste (nicht Konfiguration+).
            Ist er <strong>inaktiv</strong>, verschwindet der Konfigurator für alle Nutzer – nur Admins mit
            aktivem Entwicklermodus sehen ihn weiterhin.
          </Typography>
          <FormControlLabel
            control={<Switch checked={configuratorVisible} onChange={handleToggleConfigurator} disabled={configVisSaving} color="primary" />}
            label={configuratorVisible ? 'Aktiv' : 'Inaktiv'}
          />
        </Paper>
      )}

      {/* ── Bestätigung der globalen Umstellung ── */}
      <Dialog open={globalDialogOpen} onClose={() => setGlobalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#f59e0b' }} />
          Hauptdatenbank für alle umstellen?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" sx={{ mb: 2 }}>
              Du stellst die Datenbank-Umgebung von <strong>{dbEnvironmentLabel(globalProfile)}</strong> auf{' '}
              <strong>{dbEnvironmentLabel(globalPick)}</strong> um.
            </Typography>

            <Typography variant="body2" component="div" sx={{ mb: 2 }}>
              Das bedeutet:
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                <li><strong>Jeder Benutzer</strong> arbeitet ab sofort auf {dbEnvironmentLabel(globalPick)} — nicht nur du.</li>
                <li>
                  {overrideCount > 0
                    ? `${overrideCount} persönlich gewählte Umgebung(en) werden aufgehoben.`
                    : 'Persönlich gewählte Umgebungen werden aufgehoben.'}
                </li>
                <li>Alle Scans, Score Cards und Watchdog-Läufe schreiben ab dann in diese Datenbank.</li>
                <li>Laufende Sitzungen wechseln mit der nächsten Anfrage — ohne Neuanmeldung.</li>
              </ul>
            </Typography>

            <Alert severity="warning" sx={{ mt: 1 }}>
              Nur umstellen, wenn <strong>{dbEnvironmentLabel(globalPick)}</strong> geprüft ist und den
              vollständigen Datenstand enthält. Ein falscher Griff trifft alle Nutzer sofort.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGlobalDialogOpen(false)} disabled={globalSaving}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGlobal}
            disabled={globalSaving}
            sx={{ bgcolor: '#f59e0b', color: colors.black, '&:hover': { bgcolor: '#d97706' } }}
          >
            Ja, für alle auf {dbEnvironmentLabel(globalPick)} umstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
